import { inventoryService } from "../../../../services/inventoryService/index.js";
import { getProductsInformation } from "../../../../services/productService/getProductsInformation.js";
import { getProductActions } from "../../../../services/searchService/LucidWorks/lib.js";
import { useState, useEffect, useMemo } from "react";
import { getProductTileConfigData } from "../../../Product/ProductTile/ContentStack/index.js";
import { DEFAULT_MAX_SLIDES } from "./constants.js";
const getPublishedItems = (populatedProductData, callProductSummaryAPI) => {
  return Array.from(
    new Set(
      populatedProductData.productApiResponses.filter((p) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const productIsBuyable = callProductSummaryAPI ? (_c = (_b = (_a = p == null ? void 0 : p.productApiResponseData) == null ? void 0 : _a.productData) == null ? void 0 : _b[0]) == null ? void 0 : _c.isPublished : (_h = (_g = (_f = (_e = (_d = p == null ? void 0 : p.productApiResponseData) == null ? void 0 : _d.data) == null ? void 0 : _e.products) == null ? void 0 : _f.catalogData) == null ? void 0 : _g[0]) == null ? void 0 : _h.published;
        return productIsBuyable;
      }).flatMap((p) => p.inventoryItemNumbers).map(String)
    )
  );
};
const batchCallInventory = async (publishedInventoryNumbers, productTileConfigData, signal, cookieContext) => {
  let initialResults = [];
  const remainingItemNumbers = publishedInventoryNumbers.slice();
  while (remainingItemNumbers.length > 0) {
    const batchItemNumbers = remainingItemNumbers.splice(
      0,
      productTileConfigData.inventoryApiConfig.requestItemCount
    );
    const response = await inventoryService(
      batchItemNumbers,
      productTileConfigData.inventoryApiConfig,
      signal,
      cookieContext
    );
    let validBatchResponses = [];
    if (response && response.ok) {
      validBatchResponses = await response.json();
    } else {
      console.error("Inventory fetch failed", response);
    }
    initialResults = [...initialResults, ...validBatchResponses];
  }
  return initialResults;
};
function useProductData({
  inventoryAware,
  itemNumbers,
  locale,
  productTileConfigData,
  concurrency = 5,
  bdWHNumber,
  initialDisplayCount = DEFAULT_MAX_SLIDES,
  // Default to 5 items for initial display
  product_tile_data_source,
  isProductDetailsPage = false,
  cookieContext,
  isUserLoggedIn,
  itemCuration
}) {
  var _a, _b;
  const [isProductDataLoading, setIsProductDataLoading] = useState(true);
  const [populatedProductData, setPopulatedProductData] = useState();
  const [inventoryData, setInventoryData] = useState();
  const [populatedProductDataError, setPopulatedProductDataError] = useState();
  const [allProductsLoaded, setAllProductsLoaded] = useState(false);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const callProductSummaryAPI = ((_b = (_a = productTileConfigData == null ? void 0 : productTileConfigData.featureToggleConfig) == null ? void 0 : _a.callProductSummaryAPI) == null ? void 0 : _b.isEnabled) || product_tile_data_source === "product_summary_api";
  useEffect(() => {
    const itemNumbersArray = typeof itemNumbers === "string" ? [itemNumbers] : itemNumbers;
    setAllProductsLoaded(false);
    setInventoryLoading(false);
    if (product_tile_data_source === "lucid_works") {
      const populateLWData = async () => {
        setIsProductDataLoading(true);
        try {
          const lwData = await getPopulatedLWProductData({
            itemNumbers: itemNumbersArray,
            locale,
            productTileConfigData,
            bdWHNumber,
            cookieContext
          });
          setPopulatedProductData(lwData);
          setAllProductsLoaded(true);
        } catch (err) {
          setPopulatedProductDataError(err);
          setPopulatedProductData(null);
          console.error("ERROR: Fetching LW Product Data");
          setAllProductsLoaded(true);
        }
        setIsProductDataLoading(false);
      };
      populateLWData();
    } else {
      const populateProductData = async () => {
        setIsProductDataLoading(true);
        try {
          const initialProductData = await getPopulatedProductData({
            concurrency,
            inventoryAware,
            itemNumbers: itemNumbersArray,
            locale,
            productTileConfigData,
            bdWHNumber,
            initialBatchSize: initialDisplayCount,
            isProductDetailsPage,
            itemCuration,
            product_tile_data_source
          });
          setPopulatedProductData(initialProductData);
          if (initialProductData.remainingResultsPromise) {
            try {
              const remainingProducts = await initialProductData.remainingResultsPromise;
              if (remainingProducts.length > 0) {
                setPopulatedProductData((prevData) => {
                  if (!prevData) return null;
                  return {
                    ...prevData,
                    productApiResponses: [
                      ...prevData.productApiResponses,
                      ...remainingProducts
                    ],
                    remainingResultsPromise: null
                  };
                });
              }
              setAllProductsLoaded(true);
            } catch (err) {
              console.error(
                "ERROR: Fetching remaining product data",
                err
              );
              setAllProductsLoaded(true);
            }
          } else {
            setAllProductsLoaded(true);
          }
          setIsProductDataLoading(false);
        } catch (err) {
          setPopulatedProductDataError(err);
          setPopulatedProductData(null);
          setIsProductDataLoading(false);
          setAllProductsLoaded(true);
          console.error("ERROR: Fetching Product Data");
        }
      };
      populateProductData();
    }
  }, [itemNumbers, product_tile_data_source]);
  useEffect(() => {
    var _a2;
    const abortController = new AbortController();
    const signal = abortController.signal;
    if (!inventoryAware || product_tile_data_source === "lucid_works" || !(productTileConfigData == null ? void 0 : productTileConfigData.inventoryApiConfig)) {
      return;
    } else {
      setInventoryLoading(true);
    }
    if (!allProductsLoaded) return;
    if (!((_a2 = populatedProductData == null ? void 0 : populatedProductData.productApiResponses) == null ? void 0 : _a2.length)) return;
    if (!(cookieContext == null ? void 0 : cookieContext.warehouse)) return;
    const fetchInventory = async () => {
      try {
        const publishedInventoryNumbers = getPublishedItems(
          populatedProductData,
          callProductSummaryAPI
        );
        if (!publishedInventoryNumbers.length) {
          setInventoryLoading(false);
          return;
        }
        const inventoryResponse = await batchCallInventory(
          publishedInventoryNumbers,
          productTileConfigData,
          signal,
          cookieContext
        );
        setInventoryData(
          inventoryResponse.reduce(
            (inventoryData2, inventoryItem) => {
              if (inventoryItem.itemNumber) {
                inventoryData2[inventoryItem.itemNumber] = inventoryItem;
              }
              return inventoryData2;
            },
            {}
          )
        );
      } catch (e) {
        const errObj = e;
        if ((errObj == null ? void 0 : errObj.name) !== "AbortError") {
          console.error("Error during inventory fetch", e);
        }
      } finally {
        setInventoryLoading(false);
      }
    };
    fetchInventory();
    return () => {
      abortController.abort();
    };
  }, [
    inventoryAware,
    product_tile_data_source,
    allProductsLoaded,
    cookieContext == null ? void 0 : cookieContext.warehouse,
    populatedProductData,
    productTileConfigData == null ? void 0 : productTileConfigData.inventoryApiConfig
  ]);
  const filteredProductData = useMemo(() => {
    var _a2, _b2, _c, _d;
    if (!populatedProductData) return null;
    if (populatedProductData && ((_b2 = (_a2 = productTileConfigData == null ? void 0 : productTileConfigData.translationsConfig) == null ? void 0 : _a2.product_display_style) == null ? void 0 : _b2.hide_out_of_stock_carousel_tiles)) {
      if (product_tile_data_source === "lucid_works") {
        return {
          ...populatedProductData,
          lwResponses: (_c = populatedProductData == null ? void 0 : populatedProductData.lwResponses) == null ? void 0 : _c.filter(
            (item) => {
              const productAction = getProductActions(
                item,
                productTileConfigData.featureToggleConfig,
                productTileConfigData.translationsConfig,
                !!isUserLoggedIn,
                cookieContext
              );
              return (productAction == null ? void 0 : productAction.type) !== "OutOfStock";
            }
          )
        };
      }
    }
    const productDataResponsesWithInventory = (_d = populatedProductData == null ? void 0 : populatedProductData.productApiResponses) == null ? void 0 : _d.map((productData) => {
      return {
        ...productData,
        hasBatchInventoryResponseData: !!inventoryData,
        // If Inventory data is set to anything, consider batch successful to avoid individual tiles calling the inventory
        // Filter the inventoryResponseData down to the relevant item numbers
        inventoryResponseData: productData.inventoryItemNumbers.reduce(
          (inventoryResponseData, itemNumber) => {
            const inventoryItem = inventoryData == null ? void 0 : inventoryData[itemNumber];
            if (inventoryItem) {
              inventoryResponseData.push(inventoryItem);
            }
            return inventoryResponseData;
          },
          []
        )
      };
    });
    return {
      ...populatedProductData,
      productApiResponses: productDataResponsesWithInventory
    };
  }, [
    populatedProductData,
    product_tile_data_source,
    isUserLoggedIn,
    cookieContext,
    productTileConfigData,
    inventoryData
  ]);
  const isLoadingPopulatedProductData = isProductDataLoading || (inventoryAware ? inventoryLoading : false);
  return {
    isLoadingPopulatedProductData,
    populatedProductData: filteredProductData,
    populatedProductDataError
  };
}
async function getPopulatedLWProductData({
  locale,
  itemNumbers,
  productTileConfigData,
  bdWHNumber,
  cookieContext
}) {
  var _a, _b, _c;
  let configData = productTileConfigData;
  if (!configData) {
    configData = await getProductTileConfigData(false, locale);
  }
  if (!configData.lucidWorksApiConfig) {
    return {
      configData,
      isLucidWorksData: true,
      lwResponses: [],
      productApiResponses: []
    };
  }
  let lwLocale = locale;
  if (lwLocale.includes("-")) {
    const langParts = lwLocale.split("-");
    if (langParts.length === 2) {
      lwLocale = `${langParts[0]}-${langParts[1].toUpperCase()}`;
    }
  }
  const bdWHNumberToUse = bdWHNumber || configData.lucidWorksApiConfig.defaultBDWarehouse;
  const bdState = ((_b = (_a = cookieContext == null ? void 0 : cookieContext.bdDeliveryLocation) == null ? void 0 : _a.bdLocation) == null ? void 0 : _b.slice(-2)) || "";
  let queryString = `q=:&fq=${encodeURIComponent(`{!terms f=group_id}`)}${itemNumbers.join(",")}&start=0&rows=${itemNumbers.length}&locale=${lwLocale}&loc=${bdWHNumberToUse}`;
  if (bdState) {
    queryString += `&userLocation=${bdState}`;
  }
  if (configData.lucidWorksApiConfig.additionalURLParams) {
    queryString += `&${configData.lucidWorksApiConfig.additionalURLParams}`;
  }
  const response = await fetch(
    `${configData.lucidWorksApiConfig.apiUrl}?${queryString}`,
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "x-api-key": configData.lucidWorksApiConfig.xApiKey
      }
    }
  );
  if (!response.ok) {
    console.error("LW FETCH ERROR", response.statusText);
    return {
      configData,
      isLucidWorksData: true,
      lwResponses: [],
      productApiResponses: []
    };
  }
  const lwResponses = await response.json();
  return {
    configData,
    isLucidWorksData: true,
    lwResponses: ((_c = lwResponses == null ? void 0 : lwResponses.response) == null ? void 0 : _c.docs) || [],
    productApiResponses: []
    // Set to empty array since Product API isn't used
  };
}
async function getPopulatedProductData({
  inventoryAware,
  locale,
  itemNumbers,
  productTileConfigData,
  concurrency = 1,
  bdWHNumber,
  initialBatchSize,
  isProductDetailsPage = false,
  itemCuration,
  product_tile_data_source
}) {
  let configData = productTileConfigData;
  if (!configData) {
    configData = await getProductTileConfigData(inventoryAware, locale);
  }
  const { initialResults, remainingResultsPromise } = await getProductsInformation(
    itemNumbers,
    configData,
    locale,
    concurrency,
    bdWHNumber,
    initialBatchSize,
    isProductDetailsPage,
    itemCuration,
    product_tile_data_source
  );
  return {
    configData,
    productApiResponses: initialResults,
    lwResponses: [],
    // Set to empty array since LW API isn't used
    remainingResultsPromise
  };
}
function batchedTiles(array, size) {
  const batched = [];
  for (let i = 0; i < array.length; i += size) {
    batched.push(array.slice(i, i + size));
  }
  return batched;
}
const calculateInitialDisplayCount = (displayMultipleTiles, maxItemsPerSlide, maxSlides) => {
  if (displayMultipleTiles && maxItemsPerSlide) {
    return maxSlides ? maxItemsPerSlide * maxSlides : maxItemsPerSlide;
  }
  if (maxSlides) {
    return maxSlides;
  }
  return DEFAULT_MAX_SLIDES;
};
export {
  batchCallInventory,
  batchedTiles,
  calculateInitialDisplayCount,
  getPopulatedLWProductData,
  getPopulatedProductData,
  getPublishedItems,
  useProductData
};
